import { Label, ListBox, ListBoxItem, Select } from '@heroui/react'

import type { SelectOption } from './form-fields'

/** A key React Aria can hold that stands for "no filter". */
const ALL = 'all'

interface FilterSelectProps {
  label: string
  options: SelectOption[]
  value: string | null
  onChange: (value: string | null) => void
  /**
   * Copy for the "everything" option — "All branches", "All statuses". Omit
   * when a choice is required, as an outlet picker is.
   */
  allLabel?: string
  isDisabled?: boolean
  /**
   * Width of the trigger. The default fits a short label; a picker holding a
   * real branch name needs more room before its text wraps onto a second line
   * and pushes the row it sits in out of alignment.
   */
  triggerClassName?: string
}

/**
 * A compact labelled dropdown for the module bar.
 *
 * Staff's branch filter established the shape — label and trigger on one line,
 * sized to its content so it can sit beside the tabs rather than under them.
 * This is that control with the branch-specific parts taken out, so the modules
 * that filter by something else get the same thing in the same place.
 */
export function FilterSelect({
  label,
  options,
  value,
  onChange,
  allLabel,
  isDisabled,
  triggerClassName = 'w-40',
}: FilterSelectProps) {
  const items = [...(allLabel ? [{ value: ALL, label: allLabel }] : []), ...options]

  return (
    <Select
      selectedKey={value ?? (allLabel ? ALL : null)}
      onSelectionChange={(key) => onChange(key === ALL || key === null ? null : String(key))}
      isDisabled={isDisabled || items.length === 0}
      // `w-auto shrink-0` overrides the field default of full width, which
      // would otherwise push this onto its own row beside the tabs.
      className="flex w-auto shrink-0 flex-row items-center gap-2"
    >
      <Label className="text-muted shrink-0 text-xs">{label}</Label>
      <Select.Trigger className={triggerClassName}>
        <Select.Value className="truncate" />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {items.map((option) => (
            <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/* -------------------------------------------------------------------------- */
/* Many at once                                                               */
/* -------------------------------------------------------------------------- */

interface FilterMultiSelectProps {
  label: string
  options: SelectOption[]
  /**
   * The chosen keys. **Empty means everything**, not nothing.
   *
   * Which is the one thing to get right about this control. A merchant who
   * deselects their last outlet has not asked for an empty screen — they have
   * cleared the filter — and every caller sends no query key at all for an empty
   * value, so the API answers with the caller's whole scope. Modelling "all" as
   * an extra selectable row instead would make it a fourth outlet that silently
   * contradicts the other three.
   */
  value: readonly string[]
  onChange: (value: string[]) => void
  /** What an empty selection reads as on the trigger — "All locations". */
  allLabel: string
  /**
   * What several read as, given the count. Defaults to "3 selected"; pass one
   * that names the thing — "3 locations" — where the label alone does not.
   */
  summaryLabel?: (count: number) => string
  isDisabled?: boolean
  triggerClassName?: string
}

/**
 * The module bar's dropdown for a filter that takes SEVERAL values.
 *
 * `FilterSelect`'s shape — label and trigger on one line, sized to its content
 * so it sits beside the tabs — over a list whose rows are checkable. The two are
 * separate components rather than one with a `multiple` flag because their
 * values are different types and their empty states mean different things: a
 * single filter's empty is `null` and is offered as a row in the list, a
 * multiple filter's empty is `[]` and is reached by unchecking.
 *
 * The trigger never lists the chosen names. Three outlet names do not fit a
 * control that has to share a row with the tabs, and a trigger that grows with
 * its selection pushes everything beside it around as the merchant works. It
 * says how many instead, and the names are one click away in the list that
 * already shows which are ticked.
 */
export function FilterMultiSelect({
  label,
  options,
  value,
  onChange,
  allLabel,
  summaryLabel = (count) => `${count} selected`,
  isDisabled,
  triggerClassName = 'w-44',
}: FilterMultiSelectProps) {
  /* Only the keys the list still offers. A branch that closes — or a scope that
     narrows under a manager mid-session — would otherwise leave an id selected
     that nothing can uncheck, and the count above the list would never agree
     with the ticks in it.

     The caller's own array is handed straight back when nothing was dropped,
     which is every render but the rare one. `Select` holds this as controlled
     state, and a fresh array identity on each render is churn it has no reason
     to see. */
  const offered = value.filter((key) => options.some((option) => option.value === key))
  const selected = offered.length === value.length ? value : offered

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((option) => option.value === selected[0])?.label ?? allLabel)
        : summaryLabel(selected.length)

  return (
    <Select
      selectionMode="multiple"
      value={selected}
      onChange={(keys) => onChange(keys.map(String))}
      isDisabled={isDisabled || options.length === 0}
      // Matches `FilterSelect`: `w-auto shrink-0` overrides the field default of
      // full width, which would push this onto its own row beside the tabs.
      className="flex w-auto shrink-0 flex-row items-center gap-2"
    >
      <Label className="text-muted shrink-0 text-xs">{label}</Label>
      <Select.Trigger className={triggerClassName}>
        {/* Our own text rather than `Select.Value`, which renders the selected
            items joined — the growing trigger this control exists to avoid. */}
        <span className="flex-1 truncate text-start">{summary}</span>
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        {/* No `selectionMode` of its own, deliberately. `Select` publishes its
            own list state on `ListStateContext`, and a `ListBox` inside one
            adopts that state rather than building its own — so the mode set on
            the `Select` above is already in force here, and a second one on this
            element would read as the thing that makes the list multiple when it
            is in fact ignored.

            It stays open as rows are ticked: React Aria's default for multiple
            selection, and the only sane one, since picking three outlets through
            a popover that shut after each would be three trips. */}
        <ListBox>
          {options.map((option) => (
            <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
              <span className="flex-1 truncate">{option.label}</span>
              <ListBox.ItemIndicator />
            </ListBoxItem>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}
